import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar, ChevronDown } from 'lucide-react';

// Sample product options - replace with actual data in real implementation
const PRODUCTS = [
  'Product Original',
  'Product Premium',
  'Product Max',
  'Product Everyday',
  'Product Complete'
];

// Sample app store options
const APP_STORES = [
  'App Store',
  'Play Store',
];

const AnalyticsFilters = ({ 
  regions, 
  selectedRegion, 
  setSelectedRegion, 
  selectedCity, 
  setSelectedCity, 
  dateRange, 
  setDateRange,
  showLocations,
  setShowLocations,
  showProducts,
  setShowProducts,
  showApps,
  setShowApps,
  showNPS,
  setShowNPS,
  selectedProduct,
  setSelectedProduct,
  selectedAppStore,
  setSelectedAppStore,
  isExpanded = false,
  toggleExpand
}) => {
  const [cities, setCities] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  // Get current date
  const today = new Date();
  
  // Common date presets
  const datePresets = [
    { label: 'Last 7 days', startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), endDate: today },
    { label: 'Last 30 days', startDate: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), endDate: today },
    { label: 'Last 90 days', startDate: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), endDate: today },
    { label: 'This year', startDate: new Date(today.getFullYear(), 0, 1), endDate: today },
    { label: 'Last year', startDate: new Date(today.getFullYear() - 1, 0, 1), endDate: new Date(today.getFullYear() - 1, 11, 31) }
  ];

  // Update cities when region changes
  useEffect(() => {
    if (selectedRegion && selectedRegion !== 'global' && regions[selectedRegion]) {
      setCities(regions[selectedRegion]);
    } else {
      setCities([]);
    }
  }, [selectedRegion, regions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [datePickerRef]);

  // Determine date range display text
  const getDateRangeText = () => {
    if (!dateRange[0] && !dateRange[1]) return 'Select date range';
    
    // Check if current selection matches a preset
    for (const preset of datePresets) {
      if (
        dateRange[0]?.toDateString() === preset.startDate.toDateString() && 
        dateRange[1]?.toDateString() === preset.endDate.toDateString()
      ) {
        return preset.label;
      }
    }
    
    // Custom date range
    if (dateRange[0] && dateRange[1]) {
      return `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`;
    } else if (dateRange[0]) {
      return `From ${dateRange[0].toLocaleDateString()}`;
    } else if (dateRange[1]) {
      return `Until ${dateRange[1].toLocaleDateString()}`;
    }
    
    return 'Select date range';
  };

  // Only one can be selected at a time (radio button behavior)
  const handleSourceChange = (source) => {
    if (source === 'locations') {
      setShowLocations(true);
      setShowProducts(false);
      setShowApps(false);
      setShowNPS(false);
    } else if (source === 'products') {
      setShowLocations(false);
      setShowProducts(true);
      setShowApps(false);
      setShowNPS(false);
    } else if (source === 'apps') {
      setShowLocations(false);
      setShowProducts(false);
      setShowApps(true);
      setShowNPS(false);
    } else if (source === 'nps') {
      setShowLocations(false);
      setShowProducts(false);
      setShowApps(false);
      setShowNPS(true);
    } else if (source === 'all') {
      setShowLocations(true);
      setShowProducts(true);
      setShowApps(true);
      setShowNPS(true);
    }
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setDateRange([start, end]);
  };

  const applyDatePreset = (preset) => {
    setDateRange([new Date(preset.startDate), new Date(preset.endDate)]);
    setShowDatePicker(false);
  };

  const resetFilters = () => {
    setSelectedRegion('global');
    setSelectedCity('');
    setDateRange([null, null]);
    setSelectedProduct('');
    setSelectedAppStore('');
    handleSourceChange('all');
  };

  // Collapsed version - single line with essential filters
  if (!isExpanded) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Data Type Selectors - Radio button style */}
          <div className="flex items-center bg-gray-50 p-1 rounded-md">
            <button
              className={`px-3 py-1 text-xs rounded-md ${
                showLocations && !showProducts && !showApps && !showNPS
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => handleSourceChange('locations')}
            >
              Locations
            </button>
            <button
              className={`px-3 py-1 text-xs rounded-md ${
                !showLocations && showProducts && !showApps && !showNPS
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => handleSourceChange('products')}
            >
              Products
            </button>
            <button
              className={`px-3 py-1 text-xs rounded-md ${
                !showLocations && !showProducts && showApps && !showNPS
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => handleSourceChange('apps')}
            >
              Apps
            </button>
            <button
              className={`px-3 py-1 text-xs rounded-md ${
                !showLocations && !showProducts && !showApps && showNPS
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => handleSourceChange('nps')}
            >
              NPS
            </button>
            <button
              className={`px-3 py-1 text-xs rounded-md ${
                showLocations && showProducts && showApps && showNPS
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => handleSourceChange('all')}
            >
              All
            </button>
          </div>
          
          {/* Date Range */}
          <div className="relative" ref={datePickerRef}>
            <div 
              className="flex items-center border border-gray-300 rounded-md px-2 py-1 cursor-pointer hover:border-blue-500"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Calendar className="h-3 w-3 text-gray-500 mr-1" />
              <span className="text-xs">{getDateRangeText()}</span>
              <ChevronDown className="h-3 w-3 text-gray-500 ml-1" />
            </div>
            
            {showDatePicker && (
              <div className="absolute z-10 mt-1 w-auto min-w-max bg-white rounded-md shadow-lg border border-gray-200">
                {/* Quick Select Presets (Horizontal) */}
                <div className="p-2 border-b border-gray-200">
                  <div className="flex flex-wrap gap-1">
                    {datePresets.map((preset, index) => (
                      <button
                        key={index}
                        className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-blue-50 hover:text-blue-600"
                        onClick={() => applyDatePreset(preset)}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Calendar Range Picker */}
                <div className="p-2">
                  <DatePicker
                    selected={dateRange[0]}
                    onChange={handleDateRangeChange}
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    selectsRange
                    inline
                    monthsShown={2}
                  />
                </div>
                
                <div className="flex items-center justify-between p-2 border-t border-gray-200 bg-gray-50">
                  <button
                    className="text-xs text-gray-600 hover:text-gray-800"
                    onClick={() => setDateRange([null, null])}
                  >
                    Clear
                  </button>
                  <button
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                    onClick={() => setShowDatePicker(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Location Filters - Only show when Locations is selected */}
          {showLocations && (
            <div className="flex items-center">
              <span className="text-xs text-gray-500 mr-2">Region:</span>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={selectedRegion}
                onChange={(e) => {
                  setSelectedRegion(e.target.value);
                  setSelectedCity('');
                }}
              >
                <option value="global">All Regions</option>
                {Object.keys(regions).map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
              
              {selectedRegion !== 'global' && (
                <>
                  <span className="text-xs text-gray-500 mx-2">City:</span>
                  <select
                    className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                  >
                    <option value="">All Cities</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}
          
          {/* Product Filters - Only show when Products is selected */}
          {showProducts && (
            <div className="flex items-center">
              <span className="text-xs text-gray-500 mr-2">Product:</span>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                <option value="">All Products</option>
                {PRODUCTS.map((product) => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* App Filters - Only show when Apps is selected */}
          {showApps && (
            <div className="flex items-center">
              <span className="text-xs text-gray-500 mr-2">App:</span>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={selectedAppStore}
                onChange={(e) => setSelectedAppStore(e.target.value)}
              >
                <option value="">All Apps</option>
                {APP_STORES.map((app) => (
                  <option key={app} value={app}>
                    {app}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={resetFilters}
              className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
            >
              Reset
            </button>
            
            <button
              onClick={toggleExpand}
              className="text-xs text-gray-600 hover:text-gray-800 whitespace-nowrap px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              More
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expanded version with all filters
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium">Filters</h3>
        <button
          onClick={toggleExpand}
          className="text-xs text-gray-600 hover:text-gray-800 whitespace-nowrap"
        >
          Less
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Data Type Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Data Source</label>
          <div className="flex flex-col space-y-2">
            <div className="flex items-center">
              <input
                id="location-radio"
                type="radio"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                checked={showLocations && !showProducts && !showApps && !showNPS}
                onChange={() => handleSourceChange('locations')}
              />
              <label htmlFor="location-radio" className="ml-2 text-sm text-gray-700">
                Locations (Google Reviews)
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="product-radio"
                type="radio"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                checked={!showLocations && showProducts && !showApps && !showNPS}
                onChange={() => handleSourceChange('products')}
              />
              <label htmlFor="product-radio" className="ml-2 text-sm text-gray-700">
                Products (Amazon)
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="app-radio"
                type="radio"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                checked={!showLocations && !showProducts && showApps && !showNPS}
                onChange={() => handleSourceChange('apps')}
              />
              <label htmlFor="app-radio" className="ml-2 text-sm text-gray-700">
                Apps (App Store & Play Store)
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="nps-radio"
                type="radio"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                checked={!showLocations && !showProducts && !showApps && showNPS}
                onChange={() => handleSourceChange('nps')}
              />
              <label htmlFor="nps-radio" className="ml-2 text-sm text-gray-700">
                NPS Responses
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="all-radio"
                type="radio"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                checked={showLocations && showProducts && showApps && showNPS}
                onChange={() => handleSourceChange('all')}
              />
              <label htmlFor="all-radio" className="ml-2 text-sm text-gray-700">
                All Sources
              </label>
            </div>
          </div>
        </div>
        
        {/* Date Range Filters */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Date Range</label>
          <div className="relative" ref={datePickerRef}>
            <div 
              className="flex items-center border border-gray-300 rounded-md px-3 py-1.5 cursor-pointer hover:border-blue-500"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <Calendar className="h-4 w-4 text-gray-500 mr-2" />
              <span className="text-sm">{getDateRangeText()}</span>
              <ChevronDown className="h-4 w-4 text-gray-500 ml-2" />
            </div>
            
            {showDatePicker && (
              <div className="absolute z-10 mt-1 w-auto min-w-max bg-white rounded-md shadow-lg border border-gray-200">
                {/* Quick Select Presets (Horizontal) */}
                <div className="p-3 border-b border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {datePresets.map((preset, index) => (
                      <button
                        key={index}
                        className="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-blue-50 hover:text-blue-600"
                        onClick={() => applyDatePreset(preset)}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Calendar Range Picker */}
                <div className="p-3">
                  <DatePicker
                    selected={dateRange[0]}
                    onChange={handleDateRangeChange}
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    selectsRange
                    inline
                    monthsShown={2}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 border-t border-gray-200 bg-gray-50">
                  <button
                    className="text-sm text-gray-600 hover:text-gray-800"
                    onClick={() => setDateRange([null, null])}
                  >
                    Clear
                  </button>
                  <button
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    onClick={() => setShowDatePicker(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Location Filters - Only show when Locations is selected */}
        {showLocations && (
          <div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Location Details</label>
              <select
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-2"
                value={selectedRegion}
                onChange={(e) => {
                  setSelectedRegion(e.target.value);
                  setSelectedCity('');
                }}
              >
                <option value="global">All Regions</option>
                {Object.keys(regions).map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
              
              {selectedRegion !== 'global' && (
                <select
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                >
                  <option value="">All Cities</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {/* Product Filters - Only show when Products is selected */}
        {showProducts && (
          <div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Product Details</label>
              <select
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                <option value="">All Products</option>
                {PRODUCTS.map((product) => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* App Filters - Only show when Apps is selected */}
        {showApps && (
          <div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">App Details</label>
              <select
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={selectedAppStore}
                onChange={(e) => setSelectedAppStore(e.target.value)}
              >
                <option value="">All Apps</option>
                {APP_STORES.map((app) => (
                  <option key={app} value={app}>
                    {app}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end mt-4">
        <button
          onClick={resetFilters}
          className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap px-3 py-1.5 border border-blue-300 rounded-md hover:bg-blue-50"
        >
          Reset All Filters
        </button>
      </div>
    </div>
  );
};

export default AnalyticsFilters;